package com.macrofocus.nastac

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.nastac.components.RShapesLegend
import com.macrofocus.nastac.components.link.*
import com.macrofocus.nastac.ui.Windows
import deckgl.util.calculateMercatorCentroid
import deckgl.util.calculateZoomLevel
import emotion.react.css
import external.mosaic.MosaicParent
import js.objects.unsafeJso
import kotlinx.browser.window
import mui.icons.material.*
import mui.material.Box
import mui.system.sx
import org.locationtech.jts.geom.Envelope
import react.FC
import react.Props
import react.ReactNode
import react.create
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.*

enum class ViewStyle {
    INTRO {
        override val classname: String = "intro"
        override val description: ReactNode? = null

        override val layerGroup: LayerGroup = LayerGroup.INTRO

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
        }

        override val icon: SvgIconComponent = HomeTwoTone

        override fun toString(): String {
            return "Introduction"
        }

//        override val mosaicParent: MosaicParent<Windows> = unsafeJso<MosaicParent<Windows>> {
//            direction = "row" // Horizontal split
//            splitPercentage = 100
//            first = Windows.Intro
//            second = null
//        }
        override val mosaicParent: MosaicParent<Windows> = Windows.Introduction.unsafeCast<MosaicParent<Windows>>()

    },

    GEOGRAPHY {
        override val classname: String = "geography"

        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode? = null
        override val source: List<FC<Props>> = emptyList()

        override val layerGroup: LayerGroup = LayerGroup.GEOGRAPHY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOGRAPHY.apply(dataLoader, event)
//            // SELECT ST_Envelope(ST_Union(the_geom)) AS total_envelope FROM dbe."HEG";
//            // POLYGON ((-24.48 29.92, -24.48 77.02, 68.82000000000001 77.02, 68.82000000000001 29.92, -24.48 29.92))
////            val envelope = Envelope(-24.54, 68.94, 33.64, 77.02)
//            val envelope = Envelope(-24.48, 68.82000000000001, 29.92, 77.02)
//            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight)
//            dataLoader.threeDZoom.value = zoom
//            val centroid = calculateMercatorCentroid(envelope)
//            dataLoader.threeDLatitude.value = centroid.y
//            dataLoader.threeDLongitude.value = centroid.x
        }

        override val icon: SvgIconComponent = MapTwoTone

        override fun toString(): String {
            return "Geographical Data"
        }
    },

    CSHAPES {
        override val classname: String = "cshapes"

        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = Box.create {
            component = div
            +"Historical boundaries of independent states from the "
            +CShapesEuropeLink.create()
            +" dataset going back to 1816. Learn more about the puzzle of reversing state size in Chapter 5."
        }
        override val source: List<FC<Props>> = listOf(CShapesEuropeLink)

        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOGRAPHY || viewStyle == ViewStyle.CSHAPES || viewStyle == ViewStyle.HEG || viewStyle == ViewStyle.RSHAPES || viewStyle == ViewStyle.PSPM

        override val layerGroup: LayerGroup = LayerGroup.GEOGRAPHY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOGRAPHY.apply(dataLoader, event)
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.cshapesEurope
        }

        override val icon: SvgIconComponent = TourTwoTone

        override fun toString(): String {
            return "State Borders"
        }
    },

    HEG {
        override val classname: String = "heg"

        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOGRAPHY || viewStyle == ViewStyle.CSHAPES || viewStyle == ViewStyle.HEG || viewStyle == ViewStyle.RSHAPES || viewStyle == ViewStyle.PSPM
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = Box.create {
            component = div
            +"How do ethnic borders align with state borders? To explore this question, we generated polygons for ethnic settlement areas from the "
            +HEGLink.create()
            +" dataset. Nationalists often seek to expand or divide states along ethnonational lines to resolve mismatches between states and nations. This process of aligning political and ethnic borders helps explain the historical trend toward smaller, more homogeneous nation-states."
        }
        override val source: List<FC<Props>> = listOf(HEGLink)

        override val layerGroup: LayerGroup = LayerGroup.GEOGRAPHY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOGRAPHY.apply(dataLoader, event)
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.heg
        }

        override val icon: SvgIconComponent = JoinInnerTwoTone

        override fun toString(): String {
            return "Ethnic Settlements"
        }
    },

    RSHAPES {
        override val classname: String = "rshapes"

        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOGRAPHY || viewStyle == ViewStyle.CSHAPES || viewStyle == ViewStyle.HEG || viewStyle == ViewStyle.RSHAPES || viewStyle == ViewStyle.PSPM
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = Box.create {
            component = div
            +"European railway growth ("
            +Box.create() {
                component = span
                sx {
                    display = Display.inlineBlock
                    width = 10.px
                    height = 10.px
                    marginRight = 2.px
                    backgroundColor = rgb(0, 0, 0)
                }
            }
            +"1834–"
            +Box.create() {
                component = span
                sx {
                    display = Display.inlineBlock
                    width = 10.px
                    height = 10.px
                    marginRight = 2.px
                    backgroundColor = rgb(245,219,76)
                }
            }
            +"1922) from the "
            +RShapesLink.create()
            +" dataset. While railways may have promoted national integration, they also likely aided separatist mobilization. In Chapter 9, we test this together with records of independence claims and secessionist conflicts (1816–1945)."
        }
        override val caption: ReactNode? = Box.create {
            sx {
                position = Position.absolute
                right = 16.px
                top = 16.px
                zIndex = integer(1000)

                width = 180.px
            }

            RShapesLegend {
            }
        }
        override val source: List<FC<Props>> = listOf(RShapesLink)

        override val layerGroup: LayerGroup = LayerGroup.GEOGRAPHY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOGRAPHY.apply(dataLoader, event)
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.rshapes
        }

        override val icon: SvgIconComponent = TrainTwoTone

        override fun toString(): String {
            return "Railroad Network"
        }
    },

    CONGRUENCE {
        override val classname: String = "congruence"

        override val visible = false
        override fun isEnabled(viewStyle: ViewStyle) = true
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = ReactNode("To resolve mismatches between states and nations, nationalists seek to expand or divide states along ethnonational lines. This alignment of political and ethnic borders helps explain the historical trend toward smaller, more homogeneous nation-states.")
        override val caption: ReactNode = ReactNode("Generated polygons for ethnic settlement areas")
        override val source: List<FC<Props>> = listOf(HEGLink)

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            dataLoader.borders.value = false
            dataLoader.ethnicity.value = true
            dataLoader.pspm.value = false
            dataLoader.railway.value = false
            dataLoader.geopolitics.value = false

            dataLoader.minYear.value = 1790
            dataLoader.maxYear.value = 2017
            if(dataLoader.shiftValue.value < 1790 || dataLoader.shiftValue.value > 2017) {
                dataLoader.shiftValue.value = 1790
            }

            dataLoader.population.value = false
            dataLoader.depthTest.value = false

            // SELECT ST_Envelope(ST_Union(the_geom)) AS total_envelope FROM dbe."HEG";
            // POLYGON ((-24.48 29.92, -24.48 77.02, 68.82000000000001 77.02, 68.82000000000001 29.92, -24.48 29.92))
//            val envelope = Envelope(-24.54, 68.94, 33.64, 77.02)
            val envelope = Envelope(-24.48, 68.82000000000001, 29.92, 77.02)
            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight)
            dataLoader.threeDZoom.value = zoom
            val centroid = calculateMercatorCentroid(envelope)
            dataLoader.threeDLatitude.value = centroid.y
            dataLoader.threeDLongitude.value = centroid.x

//            dataLoader.threeDZoom.value = 4.0
            dataLoader.threeDPitch.value = 0.0 // 60.0
        }

        override val icon: SvgIconComponent = JoinInnerTwoTone

        override fun toString(): String {
            return "State-Nation Congruence"
        }
    },

    TILLY {
        override val classname: String = "tilly"

        override val visible = true

        override val description: ReactNode =
            Box.create {
                component = div
                +"How did states expand and dissolve in early modern Europe? Following Tilly’s approach, we begin in 1490 by identifying each state’s "
                +Box.create() {
                    component = span
                    sx {
                        display = Display.inlineBlock
                        width = 10.px
                        height = 10.px
                        marginRight = 2.px
                        backgroundColor = rgb(0xEE, 0xD5, 0xB7, 255.0 / 127)
                    }
                }
                +"core territory and then track its subsequent expansion. Newly acquired lands are classified as "
                +Box.create() {
                    component = span
                    sx {
                        display = Display.inlineBlock
                        width = 10.px
                        height = 10.px
                        marginRight = 2.px
                        backgroundColor = rgb(255, 0, 0, 255.0 / 200)
                    }
                }
                +"war gains, "
                +Box.create() {
                    component = span
                    sx {
                        display = Display.inlineBlock
                        width = 10.px
                        height = 10.px
                        marginRight = 2.px
                        backgroundColor = rgb(0x66, 0xCD, 0x00, 255.0 / 200)
                    }
                }
                +"peace gains, or "
                +Box.create() {
                    component = span
                    sx {
                        display = Display.inlineBlock
                        width = 10.px
                        height = 10.px
                        marginRight = 2.px
                        backgroundColor = rgb(0, 0, 255, 255.0 / 200)
                    }
                }
                span {
                    css {
                        fontStyle = FontStyle.italic
                    }
                    +"terra nullius"
                }
                +" gains,"
                +" based on the dyadic classification. For details, see Chapter 4, “War and State Formation in Early Modern Europe.”"
            }
        override val caption: ReactNode? = null // "War-related and peaceful territorial growth"
        override val source: List<FC<Props>> = emptyList()

        override val layerGroup: LayerGroup = LayerGroup.TILLY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.TILLY.apply(dataLoader, event)
//            dataLoader.ethnicity.value = false
//            dataLoader.pspm.value = false
//            dataLoader.railway.value = false
//            dataLoader.geopolitics.value = false
//
//            dataLoader.borders.value = true
//
//            dataLoader.minYear.value = 1490
//            dataLoader.maxYear.value = 1790
//            if(dataLoader.shiftValue.value < 1490 || dataLoader.shiftValue.value > 1790) {
//                dataLoader.shiftValue.value = 1500
//            }
//
//            dataLoader.population.value = false
//            dataLoader.depthTest.value = false
//
//            // SELECT ST_Envelope(ST_Union(the_geom)) AS total_envelope FROM dbe.AbramsonmaxClippedLagged;
//            // POLYGON ((-10.48 35.4, -10.48 71.16, 66.58 71.16, 66.58 35.4, -10.48 35.4))
//            val envelope = Envelope(-10.48, 66.58, 35.4, 71.16)
//            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, (window.innerHeight * 0.6).toInt())
//            dataLoader.threeDZoom.value = zoom
//            val centroid = calculateMercatorCentroid(envelope)
//            dataLoader.threeDLatitude.value = centroid.y
//            dataLoader.threeDLongitude.value = centroid.x
//
////            dataLoader.threeDZoom.value = 4.0
//            dataLoader.threeDPitch.value = 0.0 // 60.0
        }

        override val icon: SvgIconComponent = TourTwoTone

        override fun toString(): String {
            return "War and State Formation"
        }
    },

    SIZE {
        override val classname: String = "size"

        override fun isEnabled(viewStyle: ViewStyle) = false
        override val visible = false

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
//            dataLoader.borders.value = false
//            dataLoader.pspm.value = false
//            dataLoader.railway.value = false
//            dataLoader.geopolitics.value = false

            dataLoader.ethnicity.value = true

//            dataLoader.minYear.value = 1790
//            dataLoader.maxYear.value = 2017
//            if(dataLoader.shiftValue.value < 1790 || dataLoader.shiftValue.value > 2017) {
//                dataLoader.shiftValue.value = 1790
//            }

//            dataLoader.population.value = false
//            dataLoader.depthTest.value = false
//
//            dataLoader.threeDZoom.value = 4.0
//            dataLoader.threeDPitch.value = 60.0
        }

        override val icon: SvgIconComponent = ExtensionTwoTone

        override fun toString(): String {
            return "Reversing State Size"
        }
    },

    PSPM {
        override val classname: String = "pspm"

        override val visible = true
        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOGRAPHY || viewStyle == ViewStyle.CSHAPES || viewStyle == ViewStyle.HEG || viewStyle == ViewStyle.RSHAPES || viewStyle == ViewStyle.PSPM
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = ReactNode("Can we determine the shape of state borders based on observed data? From 1886 onward, we predict border probabilities using 120 partitionings sampled from observed data. Simulations that incorporate ethnic boundaries closely reproduce Europe’s political map. For details, see Chapter 6, “Shaping States into Nations.”")
        override val caption: ReactNode? = null // "Predicted border probabilities based on 120 partitionings sampled from observed data"
        override val source: List<FC<Props>> = emptyList()

        override val layerGroup: LayerGroup = LayerGroup.GEOGRAPHY

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOGRAPHY.apply(dataLoader, event)
//            dataLoader.borders.value = false
//            dataLoader.ethnicity.value = false
//            dataLoader.railway.value = false
//            dataLoader.geopolitics.value = false
//
//            dataLoader.pspm.value = true
//
//            dataLoader.minYear.value = 1790
//            dataLoader.maxYear.value = 2017
//            if(dataLoader.shiftValue.value < 1790 || dataLoader.shiftValue.value > 2017) {
//                dataLoader.shiftValue.value = 1790
//            }
//
//            dataLoader.population.value = false
//            dataLoader.depthTest.value = false
//
//            // ogrinfo -al -so src/jsMain/resources/edges.geojson
//            // Extent: (-23.254574, 36.278405) - (65.181322, 70.894413)
//            val envelope = Envelope(-23.254574, 65.181322, 36.278405, 70.894413)
//            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight)
//            dataLoader.threeDZoom.value = zoom
//            val centroid = calculateMercatorCentroid(envelope)
//            dataLoader.threeDLatitude.value = centroid.y
//            dataLoader.threeDLongitude.value = centroid.x
//
////            dataLoader.threeDZoom.value = 3.5
//            dataLoader.threeDPitch.value = 0.0
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.pspm
        }

        override val icon: SvgIconComponent = ShapeLineTwoTone

        override fun toString(): String {
            return "Predicted Borders"
        }
    },

    RESTORE {
        override val classname: String = "restore"

        override fun isEnabled(viewStyle: ViewStyle) = false
        override val visible = false

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            dataLoader.borders.value = true
            dataLoader.pspm.value = false
            dataLoader.railway.value = false
            dataLoader.geopolitics.value = false

            dataLoader.ethnicity.value = true

            dataLoader.minYear.value = 1790
            dataLoader.maxYear.value = 2017
            if(dataLoader.shiftValue.value < 1790 || dataLoader.shiftValue.value > 2017) {
                dataLoader.shiftValue.value = 1790
            }

            dataLoader.population.value = false
            dataLoader.depthTest.value = false

            dataLoader.threeDZoom.value = 4.0
            dataLoader.threeDPitch.value = 60.0
        }

        override val icon: SvgIconComponent = CenterFocusWeakTwoTone

        override fun toString(): String {
            return "Restorative Nationalism"
        }
    },

//    POPULATION {
//        override val classname: String = "population"
//
//        override val enabled = false
//
//        override fun apply(dataLoader: DataLoader) {
//            dataLoader.borders.value = false
//            dataLoader.ethnicity.value = false
//            dataLoader.railway.value = false
//
//            dataLoader.minYear.value = 1945
//            dataLoader.maxYear.value = 2017
//            if(dataLoader.shiftValue.value < 1945 || dataLoader.shiftValue.value > 2017) {
//                dataLoader.shiftValue.value = 1945
//            }
//
//            dataLoader.population.value = true
//            dataLoader.depthTest.value = true
//        }
//
//        override val icon: SvgIconComponent = JoinFullTwoTone
//
//        override fun toString(): String {
//            return "Population"
//        }
//    },

    RAILWAYS {
        override val classname: String = "railways"

        override val visible = false
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = ReactNode("While railways may have promoted national integration, they also likely aided separatist mobilization. We test this using geospatial data on European railway growth (1834–1922) and records of independence claims and secessionist conflicts (1816–1945).")
        override val caption: ReactNode = ReactNode("Geographic data on yearly railway construction")

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            dataLoader.borders.value = false
            dataLoader.ethnicity.value = false
            dataLoader.pspm.value = false
            dataLoader.geopolitics.value = false

            dataLoader.railway.value = true

            dataLoader.minYear.value = 1834
            dataLoader.maxYear.value = 1921
            if(dataLoader.shiftValue.value < 1834 || dataLoader.shiftValue.value > 1921) {
                dataLoader.shiftValue.value = 1921
            }

            dataLoader.population.value = false
            dataLoader.depthTest.value = false

            // SELECT ST_Envelope(ST_Union(geometry)) AS total_envelope FROM import.rshapes;
            // POLYGON ((-10.05871499 31.73291734, -10.05871499 68.43076529, 59.70974073 68.43076529, 59.70974073 31.73291734, -10.05871499 31.73291734))
            val envelope = Envelope(-10.05871499, 59.70974073, 31.73291734, 68.43076529)
            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight)
            dataLoader.threeDZoom.value = zoom
            val centroid = calculateMercatorCentroid(envelope)
            dataLoader.threeDLatitude.value = centroid.y
            dataLoader.threeDLongitude.value = centroid.x

//            dataLoader.threeDZoom.value = 3.5
            dataLoader.threeDPitch.value = 0.0
        }

        override val icon: SvgIconComponent = TrainTwoTone

        override fun toString(): String {
            return "Railways"
        }
    },

    GEOPOLITICS {
        override val classname: String = "geopolitics"

        override val visible = true
        override fun isEnabled(viewStyle: ViewStyle) = true
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode? = null
        override val caption: ReactNode? = null
        override val source: List<FC<Props>> = emptyList()

        override val layerGroup: LayerGroup = LayerGroup.GEOPOLITICS

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOPOLITICS.apply(dataLoader, event)
//            dataLoader.borders.value = false
//            dataLoader.ethnicity.value = false
//            dataLoader.pspm.value = false
//            dataLoader.railway.value = false

//            dataLoader.geopolitics.value = true
//            dataLoader.geopolitics_kin.value = false

//            dataLoader.minYear.value = 1945
//            dataLoader.maxYear.value = 2023
//            if(dataLoader.shiftValue.value < 1945 || dataLoader.shiftValue.value > 2023) {
//                dataLoader.shiftValue.value = 1945
//            }

//            dataLoader.population.value = false
//            dataLoader.depthTest.value = false

           // val envelope = Envelope(5.9559, 10.4921, 45.8179, 47.8085)
            // SELECT ST_Envelope(ST_Union(geometry)) AS total_envelope FROM import.cshapes
            // POLYGON ((-180 -55.90222696824397, -180 83.11387316122486, 180 83.11387316122486, 180 -55.90222696824397, -180 -55.90222696824397))
//            val envelope = Envelope(-180.0, 180.0, -55.90222696824397, 83.11387316122486)
//            val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight)
//            dataLoader.threeDZoom.value = zoom
//            val centroid = calculateMercatorCentroid(envelope)
//            dataLoader.threeDLatitude.value = centroid.y
//            dataLoader.threeDLongitude.value = centroid.x
////            dataLoader.threeDZoom.value = 2.0
//            dataLoader.threeDPitch.value = 0.0
        }

        override val icon: SvgIconComponent = TrendingUpTwoTone

        override fun toString(): String {
            return "The Return of Geopolitics"
        }
    },

    GEOPOLITICS_EXCL {
        override val classname: String = "geopolitics_excl"

        override val visible = true
        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOPOLITICS || viewStyle == ViewStyle.GEOPOLITICS_EXCL || viewStyle == ViewStyle.GEOPOLITICS_KIN
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = Box.create() {
            component = div
            +"How does ethnopolitical exclusion relate to populations living under alien rule? The share of country populations exposed to alien rule is plotted against the population share experiencing ethnopolitical exclusion — "
            +Box.create() {
                component = span
                sx {
                    display = Display.inlineBlock
                    width = 10.px
                    height = 10.px
                    marginRight = 2.px
                    backgroundColor = Color("#7F0000")
                }
            }
            +"darker tones indicate higher exclusion, most evident in Central Africa, the Middle East, and parts of Central and South Asia. For details, see Chapter 11, “Conclusions for Research and Practice.”"
        }
        override val caption: ReactNode? = null // "Share of country populations exposed to alien rule in 2023"
        override val source: List<FC<Props>> = listOf(CShapesLink, EPRLink)

        override val layerGroup: LayerGroup = LayerGroup.GEOPOLITICS

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOPOLITICS.apply(dataLoader, event)
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.geopolitics
        }

        override fun excludeLayerVisible(dataLoader: DataLoader): List<MutableProperty<Boolean>>? {
            return listOf(dataLoader.geopolitics_kin)
        }

        override val icon: SvgIconComponent = CrisisAlertTwoTone

        override fun toString(): String {
            return "Excluded Population"
        }
    },

    GEOPOLITICS_KIN {
        override val classname: String = "geopolitics_kin"

        override val visible = true
        override fun isEnabled(viewStyle: ViewStyle) = viewStyle == ViewStyle.GEOPOLITICS || viewStyle == ViewStyle.GEOPOLITICS_EXCL || viewStyle == ViewStyle.GEOPOLITICS_KIN
        override val mosaicParent: MosaicParent<Windows> = Windows.Geography.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode = Box.create() {
            +"How does the presence of powerful kin relate to populations living under alien rule? The share of country populations with powerful kin exposed to alien rule is plotted against the population share — "
                    +Box.create() {
                        component = span
                        sx {
                            display = Display.inlineBlock
                            width = 10.px
                            height = 10.px
                            marginRight = 2.px
                            backgroundColor = Color("#7F0000")
                        }
                    }
            +"darker tones indicate higher exclusion. The risk of irredentism is mostly concentrated in Asia and the Middle East. For details, see Chapter 11, “Conclusions for Research and Practice.”"
        }
        override val caption: ReactNode? = null // "Share of country populations exposed to alien rule in 2023"
        override val source: List<FC<Props>> = listOf(CShapesLink, EPRLink)

        override val layerGroup: LayerGroup = LayerGroup.GEOPOLITICS

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
            LayerGroup.GEOPOLITICS.apply(dataLoader, event)
        }

        override fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean> {
            return dataLoader.geopolitics_kin
        }

        override fun excludeLayerVisible(dataLoader: DataLoader): List<MutableProperty<Boolean>>? {
            return listOf(dataLoader.geopolitics)
        }

        override val icon: SvgIconComponent = BoltTwoTone

        override fun toString(): String {
            return "... with Powerful Kin"
        }
    },

    REPLICATION {
        override val classname: String = "replication"

        override fun isEnabled(viewStyle: ViewStyle) = true
        override val mosaicParent: MosaicParent<Windows> = Windows.Replication.unsafeCast<MosaicParent<Windows>>()

        override val description: ReactNode? = null

        override val layerGroup: LayerGroup = LayerGroup.REPLICATION

        override fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?) {
        }

        override val icon: SvgIconComponent = FolderCopyTwoTone

        override fun toString(): String {
            return "Replication Material"
        }

//        override val mosaicParent: MosaicParent<Windows> = unsafeJso<MosaicParent<Windows>> {
//            direction = "row" // Horizontal split
//            splitPercentage = 100
//            first = Windows.Intro
//            second = null
//        }

    };

    abstract val classname : String
    open val visible = true
    open fun isEnabled(viewStyle: ViewStyle) = true

    open val layerGroup: LayerGroup? = null
    open fun layerVisible(dataLoader: DataLoader): MutableProperty<Boolean>? = null
    open fun excludeLayerVisible(dataLoader: DataLoader): List<MutableProperty<Boolean>>? = null

    open val description: ReactNode? = ReactNode("Nulla facilisi. Phasellus sollicitudin nulla et quam mattis feugiat. Aliquam eget maximus est, id dignissim quam.")
    open val caption: ReactNode? = null
    open val source: List<FC<Props>>? = emptyList()

    abstract fun apply(dataLoader: DataLoader, event: PropertyEvent<ViewStyle>?)
    abstract val icon: SvgIconComponent

    open val mosaicParent: MosaicParent<Windows> = unsafeJso<MosaicParent<Windows>> {
//            direction = "row" // Horizontal split
//            splitPercentage = 20
//            first = Windows.Query
//            second = jso<MosaicParent<Windows>> {
//                direction = "column" // Vertical split
//                splitPercentage = 80
//                first = Windows.Geography
//                second = Windows.TimeSeries
//            }
        direction = "column" // Horizontal split
        splitPercentage = 60
        first = Windows.Geography
        second = Windows.TimeSeries
    }
}